import { useEffect, useState } from "react";
import { SimpleOption } from "../../../components/input/SimpleListbox";
import {
	ProctorCourseViewModel,
	useLazyGetCourseOptionsForTeacherForReportsQuery,
	useLazyGetTeacherOptionsForSchoolForReportsQuery,
} from "./adminReportsApiSlice";

interface UseTeacherAndCourseOptionsArgs {
	schoolFilter: number;
	teacherFilter: number;
	courseFilter: number;
	setTeacherFilter: (value: number) => void;
	setCourseFilter: (value: number) => void;
}

// Cascading teacher -> course option fetching for the Course-level reports tab.
// Teachers are fetched whenever the selected school changes, and courses are
// fetched whenever the selected teacher changes. Each level defaults to the
// first available option if nothing is currently selected.
export function useTeacherAndCourseOptions({
	schoolFilter,
	teacherFilter,
	courseFilter,
	setTeacherFilter,
	setCourseFilter,
}: UseTeacherAndCourseOptionsArgs) {
	const [teacherOptions, setTeacherOptions] = useState<SimpleOption[]>([]);
	const [courseOptions, setCourseOptions] = useState<ProctorCourseViewModel[]>([]);

	const [callGetTeachersBySchool] = useLazyGetTeacherOptionsForSchoolForReportsQuery();
	const [callGetCoursesByTeacher] = useLazyGetCourseOptionsForTeacherForReportsQuery();

	useEffect(() => {
		if (!schoolFilter || schoolFilter === 0) {
			setTeacherOptions([]);
			return;
		}

		callGetTeachersBySchool(schoolFilter)
			.unwrap()
			.then((teachers) => {
				if (!teachers) return;

				const options = teachers.map(
					(t) => ({ value: t.id, label: t.name }) as SimpleOption
				);

				if (!teacherFilter && teachers.length > 0) {
					setTeacherFilter(teachers[0].id);
				}

				setTeacherOptions(options);
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [schoolFilter]);

	useEffect(() => {
		if (!teacherFilter || teacherFilter === 0 || !schoolFilter) {
			setCourseOptions([]);
			return;
		}

		callGetCoursesByTeacher({ proctorId: teacherFilter, schoolId: schoolFilter })
			.unwrap()
			.then((courses) => {
				if (!courses) return;

				if (!courseFilter && courses.length > 0) {
					setCourseFilter(courses[0].courseId);
				}

				setCourseOptions(courses);
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [teacherFilter]);

	return { teacherOptions, courseOptions };
}
